/**
 * Lecture d'une saisie de coordonnées : degrés décimaux ou degrés-minutes-secondes.
 * Aucune dépendance d'exécution : ce module se teste sans navigateur.
 *
 * L'ordre et le signe manquants se déduisent de l'EMPRISE de la grille, quand elle
 * le permet : si les valeurs absolues possibles de la latitude et de la longitude
 * ne se recouvrent pas (Madagascar : 10–29 et 40–54), une valeur ne peut être que
 * l'une ou l'autre. Sinon, l'usage GPS s'applique : latitude, puis longitude.
 *
 * Les messages sont des CODES (voir textes.ts) : le module se traduit sans toucher
 * à cette logique.
 */

export type Emprise = readonly [number, number, number, number]   // ouest, sud, est, nord

export type Remarque = 'dms' | 'ordre' | 'signe_lat' | 'signe_lon'

export type CodeErreur =
  | 'vide' | 'nombres' | 'coordonnees' | 'minutes' | 'secondes'
  | 'hors_emprise' | 'hemisphere_lat' | 'hemisphere_lon'

export interface Coordonnees {
  lat: number
  lon: number
  remarques: Remarque[]
}

export type Lecture =
  | { ok: true; valeur: Coordonnees }
  | { ok: false; erreur: CodeErreur; detail: Record<string, string | number> }

/** Tolérance autour de l'emprise pour reconnaître un axe (degrés). */
const MARGE_AXE = 1

type Hemisphere = 'N' | 'S' | 'E' | 'O'

interface Composante {
  valeur: number
  hemisphere: Hemisphere | null
  sexagesimal: boolean
}

function nombre(s: string): number {
  return Number.parseFloat(s.replace(',', '.'))
}

function normaliser(saisie: string): string {
  return saisie
    .toUpperCase()
    .replace(/[’′‘`´]/g, "'")
    .replace(/[”″“]|''/g, '"')
    .replace(/[º˚]/g, '°')
    .replace(/W/g, 'O')          // West → Ouest : un seul code interne
    .trim()
}

const echec = (erreur: CodeErreur, detail: Record<string, string | number> = {}): Lecture => ({ ok: false, erreur, detail })

function lireDecimal(s: string): Composante[] | Lecture {
  const nombres = s.match(/-?\d+(?:[.,]\d+)?/g) ?? []
  if (nombres.length !== 2) return echec('nombres', { n: nombres.length })
  return nombres.map((n) => ({ valeur: nombre(n), hemisphere: null, sexagesimal: false }))
}

/**
 * « 18°54'49"S 47°32'10"E », « S 18 54 49.5 E 47 32 10 », « 18°54.82'S, 47°32.17'E »,
 * « -18°54'49" 47°32'10" ».
 */
function lireSexagesimal(s: string): Composante[] | Lecture {
  const composantes: Composante[] = []
  let nombres: { v: number; unite: string | undefined }[] = []
  let hemisphere: Hemisphere | null = null
  let enTete = false

  const clore = (): Lecture | null => {
    if (!nombres.length) return null
    let deg = 0, min = 0, sec = 0
    if (nombres.some((n) => n.unite)) {
      for (const n of nombres) {
        if (n.unite === "'") min = n.v
        else if (n.unite === '"') sec = n.v
        else deg = n.v
      }
    } else {
      [deg, min = 0, sec = 0] = nombres.map((n) => n.v)
    }
    if (min < 0 || min >= 60) return echec('minutes', { valeur: min })
    if (sec < 0 || sec >= 60) return echec('secondes', { valeur: sec })
    const signe = deg < 0 || Object.is(deg, -0) ? -1 : 1
    composantes.push({ valeur: signe * (Math.abs(deg) + min / 60 + sec / 3600), hemisphere, sexagesimal: nombres.length > 1 })
    nombres = []
    hemisphere = null
    enTete = false
    return null
  }

  for (const [, num, unite, lettre] of s.matchAll(/(-?\d+(?:[.,]\d+)?)\s*(°|'|")?|([NSEO])/g)) {
    if (lettre) {
      const h = lettre as Hemisphere
      if (nombres.length && !enTete && hemisphere === null) {
        hemisphere = h                      // après les nombres : « 18°54'S »
        const e = clore(); if (e) return e
      } else {
        const e = clore(); if (e) return e
        hemisphere = h                      // avant les nombres : « S18°54' »
        enTete = true
      }
      continue
    }
    const nouveauDegre = unite === '°' || (!unite && nombres.length === 3)
    if (nouveauDegre && nombres.length) { const e = clore(); if (e) return e }
    nombres.push({ v: nombre(num), unite })
  }
  const e = clore(); if (e) return e
  if (composantes.length !== 2) return echec('coordonnees', { n: composantes.length })
  return composantes
}

/** Plage des valeurs absolues d'un axe, si l'emprise ne traverse pas l'équateur / Greenwich. */
function plageAbsolue(a: number, b: number): [number, number] | null {
  if (a < 0 && b > 0) return null
  const x = Math.abs(a), y = Math.abs(b)
  return [Math.max(0, Math.min(x, y) - MARGE_AXE), Math.max(x, y) + MARGE_AXE]
}

function dans(v: number, p: [number, number] | null): boolean {
  return p !== null && Math.abs(v) >= p[0] && Math.abs(v) <= p[1]
}

export function lireCoordonnees(saisie: string, emprise?: Emprise): Lecture {
  const s = normaliser(saisie)
  if (!s) return echec('vide')

  const lu = /[°'"NSEO]/.test(s) ? lireSexagesimal(s) : lireDecimal(s)
  if (!Array.isArray(lu)) return lu
  const [a, b] = lu
  const remarques: Remarque[] = []
  if (lu.some((c) => c.sexagesimal)) remarques.push('dms')

  const pLat = emprise ? plageAbsolue(emprise[1], emprise[3]) : null
  const pLon = emprise ? plageAbsolue(emprise[0], emprise[2]) : null
  const deductible = pLat !== null && pLon !== null && (pLat[1] < pLon[0] || pLon[1] < pLat[0])

  const axe = (c: Composante): 'lat' | 'lon' | null =>
    c.hemisphere === 'N' || c.hemisphere === 'S' ? 'lat'
      : c.hemisphere === 'E' || c.hemisphere === 'O' ? 'lon'
        : !deductible ? null
          : dans(c.valeur, pLat) ? 'lat'
            : dans(c.valeur, pLon) ? 'lon' : null

  let axeA = axe(a), axeB = axe(b)
  if (!deductible) {
    // Sans déduction possible : usage GPS, sauf si une lettre dit le contraire.
    if (axeA === null) axeA = axeB === 'lat' ? 'lon' : 'lat'
    if (axeB === null) axeB = axeA === 'lat' ? 'lon' : 'lat'
  }
  let latC: Composante, lonC: Composante
  if (axeA === 'lat' && axeB === 'lon') { latC = a; lonC = b }
  else if (axeA === 'lon' && axeB === 'lat') { latC = b; lonC = a; remarques.push('ordre') }
  else return echec('hors_emprise', { a: a.valeur, b: b.valeur })

  let lat = latC.valeur
  let lon = lonC.valeur
  if (latC.hemisphere === 'S') lat = -Math.abs(lat)
  else if (latC.hemisphere === 'N') lat = Math.abs(lat)
  else if (emprise && emprise[3] < 0 && lat > 0) { lat = -lat; remarques.push('signe_lat') }
  else if (emprise && emprise[1] > 0 && lat < 0) { lat = -lat; remarques.push('signe_lat') }
  if (lonC.hemisphere === 'O') lon = -Math.abs(lon)
  else if (lonC.hemisphere === 'E') lon = Math.abs(lon)
  else if (emprise && emprise[2] < 0 && lon > 0) { lon = -lon; remarques.push('signe_lon') }
  else if (emprise && emprise[0] > 0 && lon < 0) { lon = -lon; remarques.push('signe_lon') }

  if (emprise) {
    if (emprise[3] < 0 && lat > 0) return echec('hemisphere_lat', { attendu: 'S' })
    if (emprise[1] > 0 && lat < 0) return echec('hemisphere_lat', { attendu: 'N' })
    if (emprise[0] > 0 && lon < 0) return echec('hemisphere_lon', { attendu: 'E' })
    if (emprise[2] < 0 && lon > 0) return echec('hemisphere_lon', { attendu: 'O' })
  }
  if (Math.abs(lat) > 90 || Math.abs(lon) > 180) return echec('hors_emprise', { a: lat, b: lon })

  return { ok: true, valeur: { lat: arrondir(lat), lon: arrondir(lon), remarques } }
}

/** Six décimales : environ 11 cm. */
export function arrondir(v: number): number {
  return Math.round(v * 1e6) / 1e6
}

/** « lat, lon » en degrés décimaux : format d'affichage et de réécriture du champ. */
export function formaterDecimal(lat: number, lon: number): string {
  return `${arrondir(lat).toFixed(6)}, ${arrondir(lon).toFixed(6)}`
}
